import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Globe2, Users, Building2, Mic2, ShieldCheck,
  Sparkles, PhoneCall, Mail, Linkedin, Facebook, Instagram, Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import LazLogo, { PARTNER_LOGOS } from "@/components/LazLogo";
import api from "@/lib/api";

const HERO_IMG = "https://customer-assets-0z36b82j.emergentagent.net/job_laz-connect/artifacts/d1p0rjl6_1000188279%20%281%29.jpg";
const ONLINE_IMG = "https://images.unsplash.com/photo-1616587226960-4a03badbe8bf?crop=entropy&cs=srgb&fm=jpg&q=85";
const TEAM_IMG = "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?crop=entropy&cs=srgb&fm=jpg&q=85";

const PILLARS = [
  { title: "Clarity", desc: "See your situation, your goals, and your obstacles without the noise." },
  { title: "Internal Dialogue", desc: "Rewire the voice in your head that decides how far you go." },
  { title: "Action", desc: "Turn insight into consistent, measurable steps." },
  { title: "Self-Leadership", desc: "Lead yourself before you lead a team, a company, or a room." },
  { title: "Discipline", desc: "The bridge between where you are and where you're capable of being." },
];

const CATEGORIES = [
  { icon: Users, key: "individual", title: "Individual Coaching", desc: "Standard, Premium, or Executive — one-on-one, at your pace.", from: "from $150 CAD/session" },
  { icon: Globe2, key: "group", title: "Group Coaching", desc: "Cohort-based coaching for friend groups, teams, or communities.", from: "from $75 CAD/person" },
  { icon: Building2, key: "corporate", title: "Corporate Coaching", desc: "Invest in the leadership and wellbeing of your team.", from: "from $1,200 CAD/month" },
  { icon: Mic2, key: "speaking", title: "Speaking, MC & Appearances", desc: "Keynotes, workshops, lunch-and-learns, and media appearances.", from: "from $700 CAD" },
];

function MarketingNav() {
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-stone-50/80 border-b border-stone-200/60">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <LazLogo size={36} />
        <nav className="hidden lg:flex items-center gap-8 text-sm text-stone-600">
          <a href="#services" className="hover:text-stone-900">Services</a>
          <a href="#pricing" className="hover:text-stone-900" data-testid="nav-link-pricing">Pricing</a>
          <a href="#how" className="hover:text-stone-900">How it works</a>
          <a href="#contact" className="hover:text-stone-900">Contact</a>
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/login">
            <Button
              data-testid="marketing-login-btn"
              variant="ghost"
              className="rounded-full text-stone-700 hover:bg-stone-100 hover:text-stone-900"
            >
              Sign in
            </Button>
          </Link>
          <Link to="/apply">
            <Button
              data-testid="marketing-cta-btn"
              className="rounded-full bg-amber-700 hover:bg-amber-800 text-stone-50 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              Start your application
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

export default function Landing() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
  }, []);

  const grouped = CATEGORIES.map((cat) => ({
    ...cat,
    items: services.filter((s) => {
      if (cat.key === "individual") return ["one_on_one", "subscription"].includes(s.type);
      return s.type === (cat.key === "group" ? "group" : cat.key === "corporate" ? "corporate" : "speaking");
    }),
  }));

  return (
    <div className="min-h-screen bg-stone-50">
      <MarketingNav />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-stone-100 border border-stone-200 text-xs text-stone-600 mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-green-800" />
              100% Online · Winnipeg, Manitoba, Canada · Serving clients worldwide
            </div>
            <h1
              data-testid="hero-headline"
              className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-none text-stone-900"
            >
              Clarity. Discipline.
              <br />
              <span className="italic text-amber-700">Leadership.</span>
            </h1>
            <p className="mt-8 text-stone-600 text-lg max-w-xl leading-relaxed">
              LAZ Coach is the mindset & executive leadership coaching practice of{" "}
              <strong className="text-stone-900">Hufrey Merveil La Zoube</strong> — helping
              individuals, groups, and corporate teams build confidence, decision-making,
              and the discipline to follow through. Delivered 100% online.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to="/apply">
                <Button
                  data-testid="hero-primary-cta"
                  className="rounded-full bg-amber-700 hover:bg-amber-800 text-stone-50 px-7 h-12 text-base shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
                >
                  Start your application <ArrowRight size={18} className="ml-2" strokeWidth={1.5} />
                </Button>
              </Link>
              <a href="#pricing">
                <Button
                  data-testid="hero-pricing-cta"
                  variant="outline"
                  className="rounded-full border-stone-300 bg-stone-50 hover:bg-stone-100 text-stone-900 px-7 h-12 text-base"
                >
                  View pricing
                </Button>
              </a>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-[2rem] overflow-hidden border border-stone-200 shadow-[0_24px_60px_rgba(28,25,23,0.08)] aspect-[4/5]">
              <img src={HERO_IMG} alt="Executive coaching" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/40 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 bg-stone-50/90 backdrop-blur-sm rounded-2xl border border-stone-200 p-5">
                <div className="text-xs uppercase tracking-[0.2em] text-amber-700 mb-2">Founder</div>
                <div className="font-serif text-xl text-stone-900 leading-snug">
                  Hufrey Merveil La Zoube
                </div>
                <div className="mt-1 text-xs text-stone-500">
                  Founder, LAZ Coach · Founder & President, Youth Leadership Movement
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5 PILLARS */}
      <section className="border-y border-stone-200 bg-stone-100/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-xs uppercase tracking-[0.2em] text-amber-700 mb-3">The framework</div>
            <h2 className="font-serif text-3xl lg:text-4xl tracking-tight leading-none text-stone-900">
              Five pillars, <span className="italic">one practice.</span>
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {PILLARS.map((p, i) => (
              <div
                key={p.title}
                data-testid={`pillar-${i}`}
                className="rounded-3xl border border-stone-200 bg-stone-50 p-6 lz-card-hover"
              >
                <div className="font-serif text-2xl text-amber-700">{String(i + 1).padStart(2, "0")}</div>
                <div className="font-serif text-xl mt-3 text-stone-900">{p.title}</div>
                <p className="mt-2 text-stone-600 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs uppercase tracking-[0.2em] text-amber-700 mb-3">Services</div>
          <h2 className="font-serif text-4xl lg:text-5xl tracking-tight leading-none text-stone-900">
            Coaching for every <span className="italic">stage.</span>
          </h2>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {CATEGORIES.map((c, i) => (
            <a
              key={c.key}
              href="#pricing"
              data-testid={`service-category-${c.key}`}
              className="lz-card-hover rounded-3xl border border-stone-200 bg-stone-50 p-7 block"
            >
              <div className="h-11 w-11 rounded-2xl bg-amber-700/10 text-amber-800 flex items-center justify-center">
                <c.icon size={20} strokeWidth={1.5} />
              </div>
              <div className="font-serif text-2xl mt-5 text-stone-900">{c.title}</div>
              <p className="mt-2 text-stone-600 text-sm leading-relaxed">{c.desc}</p>
              <div className="mt-4 text-xs uppercase tracking-[0.15em] text-amber-700">{c.from}</div>
            </a>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="bg-stone-100/50 border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-xs uppercase tracking-[0.2em] text-amber-700 mb-3">Pricing</div>
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tight leading-none text-stone-900">
              Transparent <span className="italic">CAD pricing.</span>
            </h2>
            <p className="mt-4 text-stone-500 text-sm">
              All packages are delivered 100% online. In-person by request, subject to availability.
            </p>
          </div>
          <div className="mt-12 space-y-10">
            {grouped.map((g) => (
              <div key={g.key}>
                <div className="flex items-center gap-2 mb-4">
                  <g.icon size={16} strokeWidth={1.5} className="text-amber-700" />
                  <h3 className="font-serif text-2xl text-stone-900">{g.title}</h3>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {g.items.map((s) => (
                    <div
                      key={s.id}
                      data-testid={`pricing-card-${s.id}`}
                      className="rounded-3xl bg-stone-50 border border-stone-200 p-6"
                    >
                      <div className="font-serif text-lg text-stone-900 leading-snug">{s.title}</div>
                      <div className="mt-3 flex items-baseline gap-1.5">
                        <span className="font-serif text-3xl text-amber-700">
                          {s.price ? `$${s.price.toLocaleString()}` : "Custom"}
                        </span>
                        <span className="text-xs text-stone-500">
                          {s.price ? `CAD ${s.price_unit || ""}` : s.price_unit || "quote"}
                        </span>
                      </div>
                      <p className="mt-3 text-stone-600 text-sm leading-relaxed">{s.description}</p>
                      {s.features && s.features.length > 0 && (
                        <ul className="mt-4 space-y-1.5">
                          {(s.features || []).slice(0, 3).map((f) => (
                            <li key={f} className="flex gap-2 text-xs text-stone-600">
                              <Check size={13} strokeWidth={1.5} className="text-green-700 mt-0.5 shrink-0" />
                              {f}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="rounded-[2rem] overflow-hidden border border-stone-200 aspect-[4/3]">
              <img src={ONLINE_IMG} alt="Online coaching session" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.2em] text-amber-700 mb-3">How it works</div>
            <h2 className="font-serif text-4xl lg:text-5xl leading-none tracking-tight text-stone-900">
              From application <br /><span className="italic">to first session.</span>
            </h2>
            <div className="mt-8 space-y-5">
              {[
                { t: "Apply online", d: "Fill out our pre-screening application — your goals, your challenge, your package." },
                { t: "AI-assisted pre-screening", d: "Your answers are briefed for Hufrey ahead of your review, so your first conversation counts." },
                { t: "Approval & contract", d: "Once approved, your coaching agreement and package are set up in your client dashboard." },
                { t: "Pay securely & book", d: "Pay online via Stripe, then get your session link, task list, and receipts — all in one place." },
              ].map((s, i) => (
                <div key={s.t} data-testid={`how-step-${i}`} className="flex gap-4">
                  <div className="h-9 w-9 rounded-full bg-amber-700 text-stone-50 flex items-center justify-center font-serif text-sm shrink-0">
                    {i + 1}
                  </div>
                  <div>
                    <div className="font-medium text-stone-900">{s.t}</div>
                    <div className="text-sm text-stone-600 mt-0.5">{s.d}</div>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/apply">
              <Button
                data-testid="how-cta"
                className="mt-10 rounded-full bg-amber-700 hover:bg-amber-800 text-stone-50 px-7 h-12"
              >
                Start your application <ArrowRight size={16} className="ml-2" strokeWidth={1.5} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CORPORATE */}
      <section className="bg-stone-100/50 border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="text-xs uppercase tracking-[0.2em] text-amber-700 mb-3">For organizations</div>
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tight leading-none text-stone-900">
              Invest in your team's <span className="italic">leadership.</span>
            </h2>
            <p className="mt-6 text-stone-600 leading-relaxed">
              Corporate packages are billed to the organization, not the individual employee —
              with quarterly progress reports for management and optional workshops, lunch-and-learns, or keynotes.
            </p>
            <a href="#pricing">
              <Button
                data-testid="corporate-cta"
                variant="outline"
                className="mt-8 rounded-full border-stone-300 text-stone-900 hover:bg-stone-100 px-7 h-12"
              >
                See corporate pricing
              </Button>
            </a>
          </div>
          <div className="lg:col-span-6">
            <div className="rounded-[2rem] overflow-hidden border border-stone-200 aspect-[4/3]">
              <img src={TEAM_IMG} alt="Team coaching" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 text-center">
        <div className="text-xs uppercase tracking-[0.2em] text-amber-700 mb-3">Trust & boundaries</div>
        <h2 className="font-serif text-4xl lg:text-5xl tracking-tight leading-none text-stone-900 max-w-3xl mx-auto">
          Coaching, <span className="italic">not therapy.</span> Confidential, always.
        </h2>
        <p className="mt-6 text-stone-600 max-w-2xl mx-auto text-sm leading-relaxed">
          LAZ Coach is not a licensed therapy, psychiatric, or financial advisory service. Coaching
          is a collaborative process for personal & professional growth — your information stays
          confidential, and every application goes through a human review before any commitment is made.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          {[
            { icon: ShieldCheck, label: "PIPEDA & Law 25 aligned" },
            { icon: Sparkles, label: "AI-assisted pre-screening" },
            { icon: Globe2, label: "100% online delivery" },
          ].map((b) => (
            <div key={b.label} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-stone-100 border border-stone-200 text-xs text-stone-600">
              <b.icon size={14} strokeWidth={1.5} className="text-amber-700" />
              {b.label}
            </div>
          ))}
        </div>
      </section>

      {/* PARTNERS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 border-t border-stone-200">
        <div className="text-center mb-10">
          <div className="text-xs uppercase tracking-[0.2em] text-amber-700 mb-2">Affiliated organizations</div>
          <h2 className="font-serif text-2xl text-stone-900">Proud partners</h2>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-8">
          {PARTNER_LOGOS.map((p) => (
            <div
              key={p.name}
              data-testid={`partner-logo-${p.name.replace(/\s/g, "-").toLowerCase()}`}
              className="flex items-center gap-3 rounded-2xl border border-stone-200 bg-stone-50 px-5 py-4"
            >
              <div className="h-12 w-12 rounded-xl overflow-hidden border border-stone-200 shrink-0 bg-white">
                <img src={p.url} alt={p.name} className="h-full w-full object-cover" />
              </div>
              <div>
                <div className="text-sm font-medium text-stone-900">{p.name}</div>
                <div className="text-xs text-stone-500">{p.tagline}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <footer id="contact" className="border-t border-stone-200 bg-stone-100/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-8 text-sm text-stone-500">
          <div>
            <div className="font-serif text-stone-900 text-xl">LAZ Coach</div>
            <div className="text-xs mt-1">© {new Date().getFullYear()} — Founded by Hufrey Merveil La Zoube.</div>
            <div className="text-xs mt-3">Laz.coach — registered business name, Manitoba, Canada (Registry No. 10259853).</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-stone-400 mb-3">Contact</div>
            <a href="mailto:info@lazcoach.com" data-testid="footer-email" className="flex items-center gap-2 hover:text-stone-900 mb-2">
              <Mail size={14} strokeWidth={1.5} /> info@lazcoach.com
            </a>
            <a href="tel:+12042023433" data-testid="footer-phone" className="flex items-center gap-2 hover:text-stone-900">
              <PhoneCall size={14} strokeWidth={1.5} /> 204-202-3433
            </a>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-stone-400 mb-3">Legal</div>
            <div className="flex flex-col gap-2">
              <Link to="/legal/terms" className="hover:text-stone-900" data-testid="footer-terms">Coaching terms</Link>
              <Link to="/legal/privacy" className="hover:text-stone-900" data-testid="footer-privacy">Privacy & data</Link>
              <Link to="/legal/ethics" className="hover:text-stone-900" data-testid="footer-ethics">Code of ethics</Link>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-stone-400 mb-3">Follow</div>
            <div className="flex gap-3">
              <a href="https://linkedin.com/company/laz-coach" target="_blank" rel="noopener noreferrer" data-testid="footer-linkedin" className="hover:text-stone-900">
                <Linkedin size={18} strokeWidth={1.5} />
              </a>
              <a href="https://facebook.com/lazcoach1" target="_blank" rel="noopener noreferrer" data-testid="footer-facebook" className="hover:text-stone-900">
                <Facebook size={18} strokeWidth={1.5} />
              </a>
              <a href="https://instagram.com/lazcoach1" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" className="hover:text-stone-900">
                <Instagram size={18} strokeWidth={1.5} />
              </a>
            </div>
            <div className="mt-5">
              <Link to="/login" className="hover:text-stone-900 text-xs">Sign in</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
